'use client'

import {useState, useRef, useEffect, forwardRef} from 'react'
import type {ReactNode, FormEvent, CSSProperties, ForwardedRef} from 'react'
import type {FormDataObject, Theme} from '@local/types'
import {CustomerUserError} from '@local/types/schema'
import {useTranslate} from '@local/hooks'
import {Locale, Button, ButtonSpinner} from '@local/ui/components'
import {FormContext} from '@local/stores/contexts'
import {cx} from '@local/utils/cx'

export interface FormProps {
	children: ReactNode,
	className?: string,
	autoReset: boolean,
	additionalReset: () => void,
	submitLabel?: string,
	errors?: { [errorCode: string]: string },
	errorClassName?: string,
	successClassName?: string,
	successMessage?: ReactNode,
	messageClassName?: string,
	buttonTheme?: Theme,
	buttonClassName?: string,
	onSubmit: ({form, formData, body}: {
		form: HTMLFormElement | undefined,
		formData: FormData | undefined,
		body: FormDataObject | undefined
	}) => Promise<{ errors: CustomerUserError[] } | Response>,
	style?: CSSProperties,
	hideSubmit?: boolean,
	hideSuccess?: boolean,
	onChange?: (e) => void,
}

export const Form = forwardRef(
	(
		{
			children,
			className,
			autoReset = true,
			additionalReset,
			errors,
			errorClassName,
			successClassName,
			successMessage,
			messageClassName,
			submitLabel,
			buttonTheme,
			buttonClassName,
			onSubmit,
			onChange,
			style,
			hideSubmit = false,
			hideSuccess = false,
		}: FormProps,
		ref:ForwardedRef<HTMLFormElement>,
	) => {
		const [error, setError] = useState<string | null>(null)
		const [success, setSuccess] = useState<ReactNode | null>(null)
		const [hasValidation, setHasValidation] = useState(false)
		const [isLoading, setIsLoading] = useState(false)
		const [isLoaded, setIsLoaded] = useState(false)
		const locale = useTranslate()
		const defaultError = useRef(locale('An error occured, please try again later'))
		const timeoutRef = useRef<ReturnType<typeof setTimeout>>()
		const [isErrorOrSuccess, setIsErrorOrSuccess] = useState<boolean>(false)

		const handleInvalid = (event) => {
			event.preventDefault()

			setHasValidation(true)
			setError(null)
			setSuccess(null)
		}

		useEffect(() => {
			return () => {
				if (timeoutRef.current) clearTimeout(timeoutRef.current)
			}
		}, [])

		const handleSubmit = async (event: FormEvent) => {
			event.preventDefault()

			setIsLoading(true)
			setHasValidation(true)
			setError(null)
			setSuccess(null)
			setIsErrorOrSuccess(false)

			const form = event.currentTarget as HTMLFormElement
			const formData = new FormData(form)
			const body = formDataToObject(formData)

			// submit
			const result = await onSubmit({form, formData, body})
			setIsLoading(false)
			if (result && (result.status?.toString()[0] !== '2' || result.errors?.length)) {
				const code = result.status || result.errors[0].code
				setError(((code && errors?.[code]) || result.errors[0].message || defaultError.current).trim())
				setIsErrorOrSuccess(true)

				// timeoutRef.current = setTimeout(() => {
				// 	setIsErrorOrSuccess(false)
				// }, 3000)
			} else {
				setSuccess(successMessage || result?.success)
				setIsErrorOrSuccess(true)
				setIsLoaded(true)
				timeoutRef.current = setTimeout(() => {
					setIsLoaded(false)
					// setIsErrorOrSuccess(false)
					if (autoReset) {
						setHasValidation(false)
						additionalReset?.()
						form.reset()
					}
				}, 5000)
			}
		}

		return (
			<form
				ref={ref}
				onChange={onChange}
				className={cx('relative', className)}
				style={style}
				onInvalid={handleInvalid}
				onSubmit={handleSubmit}>
				<FormContext.Provider value={{hasValidation, isLoading}}>{children}</FormContext.Provider>
				{hideSubmit && (
					<ButtonSpinner
						type='submit'
						variant='primary'
						theme={buttonTheme}
						loading={isLoading}
						loaded={isLoaded}
						disabled={isLoading || isLoaded}
						className={cx('mt-40', buttonClassName)}>
						{submitLabel || <Locale>Submit</Locale>}
					</ButtonSpinner>
				)}
				{!hideSuccess &&
					<div
						aria-hidden={!isErrorOrSuccess}
						className={cx(
							'absolute z-1 left-0 w-full text-9 mt-10 animate-fade-in uppercase transition-[opacity,visibility]',
							messageClassName,
							error && `text-red pointer-events-none ${errorClassName}`,
							success && successClassName,
							isErrorOrSuccess ? 'opacity-100 visible' : 'opacity-0 invisible',
						)}>
						{error || success}
					</div>}
			</form>
		)
	},
)

// const formDataToObject = (formData: FormData): FormDataObject => (
// 	Array.from(formData.entries()).reduce(
// 		(accumulator, [key, value]) => {
// 			return ({...accumulator, [key]: value === 'on' ? true : value})
// 		},
// 		{},
// 	)
// )

const formDataToObject = (formData: FormData): FormDataObject => (
	Array.from(formData.entries()).reduce(
		(accumulator, [key, value]) => {
			// eslint-disable-next-line no-prototype-builtins
			if (accumulator.hasOwnProperty(key)) {
				// If the key already exists, concatenate the values
				const existingValue = accumulator[key]
				const updatedValue = Array.isArray(existingValue)
					? [...existingValue, value]
					: [existingValue, value]
				return {...accumulator, [key]: updatedValue}
			} else {
				return {...accumulator, [key]: value === 'on' ? true : value}
			}
		},
		{},
	)
)
